import _ from 'lodash';
import React from 'react';
import { commitMutation, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { Analytics, Dthc, GlobalNotifications, Intent } from '../../Modules';
import styles from '../Event.scss';
import { Button } from 'semantic-ui-react';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';
import DateUtils from '../../../lib/utils/date';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import EventSegmentShow from '../EventSegment/EventSegmentShow';
import EventSegmentMobile from '../EventSegment/EventSegmentMobile';
import EventSegmentDesktop from '../EventSegment/EventSegmentDesktop';

export default class ScheduleComponent extends React.Component {
  static propTypes = {
    node: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    readOnly: PropTypes.bool
  };

  constructor(props) {
    super(props);
    this.toggleSearch = this.toggleSearch.bind(this);
    this.closeSearchHandler = this.closeSearchHandler.bind(this);
    this.eventSegmentChangeHandler = this.eventSegmentChangeHandler.bind(this);
    this.updateEventSegment = this.updateEventSegment.bind(this);
    this.resetEditEventSegment = this.resetEditEventSegment.bind(this);
    this.deleteEventSegment = this.deleteEventSegment.bind(this);
    this.newEventSegmentChangeHandler = this.newEventSegmentChangeHandler.bind(
      this
    );
    this.closeNewEventSegmentEditor = this.closeNewEventSegmentEditor.bind(
      this
    );
    this.createEventSegment = this.createEventSegment.bind(this);
    this.addEventSegment = this.addEventSegment.bind(this);
    this.resetAddEventSegment = this.resetAddEventSegment.bind(this);
    this.state = {
      showSearch: false,
      localEvent: _.cloneDeep(this.props.node),
      editingNewEventSegment: false
    };
  }

  componentWillReceiveProps(nextProps) {
    // Update localEvent if any edges are added/deleted
    const obj = _.cloneDeep(this.state);
    obj.localEvent.user_event_segments = _.cloneDeep(
      nextProps.node.user_event_segments
    );
    this.setState({ ...obj });
  }

  toggleSearch() {
    this.setState({ showSearch: !this.state.showSearch });
  }

  closeSearchHandler() {
    this.setState({ showSearch: false });
  }

  eventSegmentChangeHandler(eventSegment, idx) {
    const obj = _.cloneDeep(this.state);
    obj.localEvent.user_event_segments.edges[idx].node = eventSegment;
    this.setState({ ...obj });
  }

  updateEventSegment(idx, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to update event information',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      GlobalNotifications.show({
        message: 'Successfully updated event information',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('updated-event-segment');
    };
    const eventSegment = DateUtils.omitTimeZoneWithoutTimes(_.pick(
      nullifyEmptyStrings(
        this.state.localEvent.user_event_segments.edges[idx].node
      ),
      [
        'id',
        'title',
        'start_at',
        'end_at',
        'time_zone',
        'location',
        'line1',
        'city',
        'province',
        'postal_code',
        'room',
        'notes'
      ]
    ));

    const mutation = graphql`
      mutation ScheduleComponent_updateUserEventSegmentMutation(
        $input: UpdateUserEventSegmentInput!
      ) {
        updateUserEventSegment(input: $input) {
          user_event {
            start_at
            end_at
            time_zone
          }
          user_event_segment {
            id
            title
            start_at
            end_at
            time_zone
            all_day
            location
            line1
            line2
            city
            province
            postal_code
            room
            notes
          }
        }
      }
    `;

    const optimisticResponse = {
      updateUserEventSegment: { user_event_segment: _.cloneDeep(eventSegment) }
    };
    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...eventSegment
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  resetEditEventSegment(index) {
    const obj = _.cloneDeep(this.state);
    obj.localEvent.user_event_segments.edges[index] = _.cloneDeep(
      this.props.node.user_event_segments.edges[index]
    );
    this.setState({ ...obj });
  }

  deleteEventSegment(idx, successCb, failureCb) {
    /* eslint-disable no-unused-vars */
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to delete agenda item',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
      this.resetEditEventSegment();
      GlobalNotifications.show({
        message: 'Successfully deleted agenda item',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('deleted-event-segment');
    };

    const mutation = graphql`
      mutation ScheduleComponent_deleteUserEventSegmentMutation(
        $input: DeleteUserEventSegmentInput!
      ) {
        deleteUserEventSegment(input: $input) {
          user_event {
            start_at
            end_at
            time_zone
            user_event_segments {
              edges {
                node {
                  id
                  title
                  start_at
                  end_at
                  time_zone
                  all_day
                  location
                  line1
                  line2
                  city
                  province
                  postal_code
                  room
                  notes
                }
              }
            }
          }
          deletedUserEventSegmentId
        }
      }
    `;

    const environment = this.props.relay.environment;
    const parentID = this.props.node.id;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID,
        pathToConnection: ['user_event', 'user_event_segments'],
        deletedIDFieldName: 'deletedUserEventSegmentId',
        connectionKeys: [
          {
            key: 'ScheduleContainer_user_event_segments',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.node.user_event_segments.edges[idx].node.id
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  resetAddEventSegment() {
    const obj = _.cloneDeep(this.state);
    delete obj.localEvent.newEventSegment;
    this.setState({ ...obj });
  }

  createEventSegment(index, successCb, failureCb) {
    /* eslint-disable no-unused-vars */
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add event information',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
      const obj = _.cloneDeep(this.state);
      delete obj.localEvent.newEventSegment;
      this.setState({ ...obj });
      GlobalNotifications.show({
        message: 'Successfully added event information',
        intent: Intent.SUCCESS
      });
      Analytics.trackEvent('created-event-segment');
    };

    const mutation = graphql`
      mutation ScheduleComponent_createUserEventSegmentMutation(
        $input: CreateUserEventSegmentInput!
      ) {
        createUserEventSegment(input: $input) {
          user_event {
            start_at
            end_at
            time_zone
            ...ScheduleContainer_node
          }
        }
      }
    `;

    const eventSegment = DateUtils.omitTimeZoneWithoutTimes(_.omit(
      nullifyEmptyStrings(this.state.localEvent.newEventSegment.node),
      'all_day'
    ));

    eventSegment.title = eventSegment.title || '(Untitled Agenda Item)';

    const parentID = this.props.node.id;

    const environment = this.props.relay.environment;
    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          user_event_id: parentID,
          ...eventSegment
        }
      },
      onCompleted,
      onError
    });
  }

  addEventSegment(e) {
    e.preventDefault();
    const obj = _.cloneDeep(this.state);
    obj.editingNewEventSegment = true;
    if (!this.state.localEvent.newEventSegment) {
      obj.localEvent.newEventSegment = { node: { all_day: true } };
    }
    this.setState({ ...obj });
  }

  newEventSegmentChangeHandler(eventSegment) {
    const obj = _.cloneDeep(this.state);
    obj.localEvent.newEventSegment.node = eventSegment;
    this.setState({ ...obj });
  }

  closeNewEventSegmentEditor() {
    this.setState({ editingNewEventSegment: false });
  }

  render() {
    const event = this.props.node;
    const { localEvent, editingNewEventSegment } = this.state;
    return (
      <div>
        {event.user_event_segments.edges.length === 0 &&
          !localEvent.newEventSegment &&
          !this.props.readOnly &&
          <div style={{ textAlign: 'center', padding: '2em' }}>
            <h4>This event has no agenda items</h4>
            <br />
            <Button
              basic
              icon='plus'
              labelPosition='left'
              content='Add Time and Place'
              onClick={this.addEventSegment}
            />
          </div>}
        {!this.props.readOnly &&
          <div>
            {event.user_event_segments.edges.map((eventSegment, idx) =>
              <div
                key={eventSegment.node.id || idx}
                className={styles.verticalSpacing}
              >
                <Dthc
                  eventSegment={eventSegment.node}
                  localEventSegment={
                    localEvent.user_event_segments.edges[idx].node
                  }
                  onChange={segment => {
                    this.eventSegmentChangeHandler(segment, idx);
                  }}
                  commitUpdate={this.updateEventSegment}
                  desktop={EventSegmentDesktop}
                  mobile={EventSegmentMobile}
                  index={idx}
                  reset={this.resetEditEventSegment}
                  delete={this.deleteEventSegment}
                  viewer={this.props.viewer}
                >
                  <EventSegmentShow noBorder compact eventSegment={eventSegment.node} />
                </Dthc>
              </div>
            )}
          </div>}
        {this.props.readOnly &&
          <div>
            {event.user_event_segments.edges.map((eventSegment, idx) =>
              <div
                key={eventSegment.node.id || idx}
                className={styles.verticalSpacing}
              >
                <EventSegmentShow
                  noBorder
                  readOnly={this.props.readOnly}
                  eventSegment={eventSegment.node}
                />
              </div>
            )}
          </div>}
        <ReactCSSTransitionGroup
          transitionName='slide'
          transitionEnterTimeout={300}
          transitionLeaveTimeout={300}
        >
          {localEvent.newEventSegment &&
            editingNewEventSegment &&
            <div>
              <Dthc
                eventSegment={localEvent.newEventSegment.node}
                localEventSegment={localEvent.newEventSegment.node}
                onChange={segment => {
                  this.newEventSegmentChangeHandler(segment);
                }}
                commitUpdate={this.createEventSegment}
                desktop={EventSegmentDesktop}
                mobile={EventSegmentMobile}
                reset={this.resetAddEventSegment}
                initiallyOpen
                isNew
                delete={this.resetAddEventSegment}
                closeEditor={this.closeNewEventSegmentEditor}
                editorOpen={editingNewEventSegment}
                viewer={this.props.viewer}
              >
                <EventSegmentShow
                  eventSegment={localEvent.newEventSegment.node}
                />
              </Dthc>
            </div>}
        </ReactCSSTransitionGroup>
        {event.user_event_segments.edges.length > 0 &&
          !this.props.readOnly &&
          <div style={{ marginTop: '2em' }}>
            <a onClick={this.addEventSegment}>+ Add Time and Place</a>
          </div>}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Event/Show/ScheduleComponent.js